import React, { useEffect, useRef, useState } from 'react';
import { BsCartXFill } from 'react-icons/bs';
import { HiOutlineDownload } from 'react-icons/hi';
import XLSX from 'sheetjs-style';

import { PrimaryTitleGray } from '../../../../app/theme/tokens/colors';
import {
  GreenLabel,
  YellowLabel,
  RedLabel,
} from '../../../../shared/messages/labels';
import { Spinner } from '../../../../shared/ui/Spinners';
import { LighterGreenButton } from '../../../../shared/ui/Inputs';
import { ExpandableTable } from '../../../../shared/ui/table';
import { OrderTableData } from '../../../../types/orders/Order';
import {
  BasicFilter,
  FilterOperations,
  FilterValueType,
} from '../../../../shared/filters/basic';
import { ImageSliderPop } from '../../../../shared/overlays/imageSlider';
import { BasicPagination } from '../../../../shared/ui/pagination/basic';
import { usePortalStore } from '../../../../stores/portalStore';
import { useOrderStore } from '../../store/ordersStore';
import { formatCPF } from '../../../../utils/format';
import { getDeliveryLabel } from '../../../../utils/orders';
import { formatDate } from '../../../../utils/date';
import { getProofOfDelivery } from '../../../../services/orders/ordersService';
import { exportExcelData } from '../../../../services/orders/ordersExportService';
import { formatDocumentOrDash } from '../../../../services/patients/patientsService';
import { Tooltip } from '../../../../shared/ui/Tooltip';
import { Activity } from './activity';
import { AlarmLamp } from '../../../../shared/domain/alarm/AlarmLamp';
import { OrderDetails } from '../OrderDetails';
import { OrderEnums } from '../../../../shared/constants/OrderEnums';
import { PatientName } from '../../../../shared/domain/patient/PatientName';
import { AlarmsDataSource } from '../../../../datasources/orders/AlarmsDataSource';
import { UserDataSource } from '../../../../datasources/orders/UsersDataSource';
import { OrdersDataSource } from '../../../../datasources/orders/OrdersDataSource';
import { Product } from '../../../../types/catalog/Product';
import { ExportOrdersProgressModal } from './ExportOrderProgressModal';
import {
  ensureDistributionCentersLoaded,
  getDistributionCenterName,
  getDistributionCentersItems,
} from '../../../../stores/catalog/DistributionCenters';
import { useCompaniesCatalog } from '../../../../hooks/useCompaniesCatalog';

const orderEnums = new OrderEnums();

type CurrentUser = {
  canSeeFullOrdersTable: boolean;
  isWhiteMartins?: boolean;
  isAdmin?: boolean;
};

type SelectedFilter = {
  name: string;
  operation?: string;
  value?: unknown;
};

type FilterListItem = {
  name: string;
  operations: string[];
  values?: string[];
  type?: string;
};

type AdditionalInformationItem = {
  id: number;
  name: string;
};

type OrderRow = OrderTableData & {
  distribution_center: string;
  customer: OrderTableData['customer'] & {
    additionalInformation?: AdditionalInformationItem[];
  };
};

const uniqueFilterValues = (values: unknown[]): string[] => {
  return Array.from(
    new Set(
      values
        .map((value) => (value == null ? '' : String(value).trim()))
        .filter((value) => value !== '')
    )
  );
};

const getFilterValues = (filters: SelectedFilter[], name: string): string[] => {
  return uniqueFilterValues(
    (filters ?? [])
      .filter((filter) => filter?.name === name)
      .map((filter) => filter?.value)
  );
};

const getFirstFilterValue = (
  filters: SelectedFilter[],
  name: string
): unknown => {
  return (filters ?? []).find((filter) => filter?.name === name)?.value;
};

const getDateRanges = (
  filters: SelectedFilter[],
  name: string
): unknown[][] => {
  return (filters ?? [])
    .filter((filter) => filter?.name === name && Array.isArray(filter?.value))
    .map((filter) => filter.value as unknown[])
    .filter((range) => range[0] && range[1]);
};

const getOrderDateRangesParam = (
  filters: SelectedFilter[]
): Array<{ initial_date: string; final_date: string }> => {
  return getDateRanges(filters, 'Data').map((range) => ({
    initial_date: formatDate(range[0] as string),
    final_date: formatDate(range[1] as string),
  }));
};

const getDeliveryDateRangesParam = (
  filters: SelectedFilter[]
): Array<{
  initial_delivery_date: string;
  final_delivery_date: string;
}> => {
  return getDateRanges(filters, 'Estimativa de entrega').map((range) => ({
    initial_delivery_date: formatDate(range[0] as string),
    final_delivery_date: formatDate(range[1] as string),
  }));
};

const asSingleOrArray = (values: string[]): string | string[] | undefined => {
  if (values.length === 0) return undefined;
  return values.length === 1 ? values[0] : values;
};

const getFilterParam = (
  filters: SelectedFilter[],
  name: string
): string | string[] | undefined => {
  return asSingleOrArray(getFilterValues(filters, name));
};

const normalizeCompanyTypeFilterValue = (value: string): string => {
  const text = String(value ?? '')
    .trim()
    .toLowerCase();
  if (text === 'público' || text === 'publico' || text === 'public')
    return 'public';
  if (text === 'privado' || text === 'private') return 'private';
  return '';
};

const getCompanyTypeParam = (
  filters: SelectedFilter[]
): string | string[] | undefined => {
  return asSingleOrArray(
    uniqueFilterValues(
      getFilterValues(filters, 'Tipo de empresa')
        .map((value) => normalizeCompanyTypeFilterValue(value))
        .filter(Boolean)
    )
  );
};

const extractCnpjFromEmpresaCnpjFilter = (
  value: unknown
): string | undefined => {
  if (!value) return undefined;

  const parts = String(value).split(' - ');
  const cnpjPart = parts[parts.length - 1];
  const digits = cnpjPart.replace(/\D/g, '');

  return digits || undefined;
};

const getEmpresaCnpjParam = (
  filters: SelectedFilter[]
): string | string[] | undefined => {
  return asSingleOrArray(
    uniqueFilterValues(
      getFilterValues(filters, 'Empresa - CNPJ')
        .map((value) => extractCnpjFromEmpresaCnpjFilter(value))
        .filter(Boolean)
    )
  );
};

interface OrdersHistoryProps {
  currentUser: CurrentUser;
  onSearch: (
    filters: SelectedFilter[] | null,
    pagination?: { page: number | string; pageSize: number | string }
  ) => void;
  onOrderEdit: (order: OrderTableData) => void;
  hiddenFileInput: React.RefObject<HTMLInputElement>;
  uploadCECFile: (order: OrderTableData) => void;
  handleChangeCECFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCancelOrder: (order: OrderTableData) => void;
  onOpenActivity: (order: OrderTableData) => void;
  isLoading: boolean;
  selectedFilters: SelectedFilter[];
}

export const OrdersHistory: React.FC<OrdersHistoryProps> = ({
  currentUser,
  onSearch,
  onOrderEdit,
  hiddenFileInput,
  uploadCECFile,
  handleChangeCECFile,
  onCancelOrder,
  isLoading,
  selectedFilters,
}) => {
  type BasicFilterLooseProps = {
    fieldList: unknown;
    onSearch: unknown;
    leftSlot?: React.ReactNode;
    rightSlot?: React.ReactNode;
  };

  const BasicFilterLoose =
    BasicFilter as unknown as React.ComponentType<BasicFilterLooseProps>;

  const orders = useOrderStore(
    (state) => (state as { orders: unknown }).orders
  ) as unknown as OrderRow[];

  const rows = useOrderStore(
    (state) => (state as { rows: unknown }).rows
  ) as unknown as number;

  const page = useOrderStore((state) => (state as { page: unknown }).page);
  const pageSize = useOrderStore(
    (state) => (state as { pageSize: unknown }).pageSize
  );

  const currentPageNumber = Number(page);
  const currentPageSizeNumber = Number(pageSize);
  const canExportResponsibleOperator = Boolean(
    currentUser.canSeeFullOrdersTable || currentUser.isAdmin
  );
  const canEditOrders = Boolean(
    currentUser.isAdmin || currentUser.isWhiteMartins
  );

  const [isDownloading, setIsDownloading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportTotalOrders, setExportTotalOrders] = useState(0);
  const [exportProcessedOrders, setExportProcessedOrders] = useState(0);
  const [exportStage, setExportStage] = useState('');
  const [exportErrorCount, setExportErrorCount] = useState(0);
  const [exportCanClose, setExportCanClose] = useState(false);
  const exportCancelledRef = useRef(false);
  const [exportLimitMessage, setExportLimitMessage] = useState<string>('');

  const [openAction, setOpenAction] = useState(false);
  const [proofOfDelivery, setProofOfDelivery] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [filterList, setFilterList] = useState<FilterListItem[]>([]);

  const { companies, resolveCompanyName } = useCompaniesCatalog();

  const setCurrentAlertError = usePortalStore(
    (state) =>
      (state as { setCurrentAlertError: (message: string) => void })
        .setCurrentAlertError
  );

  const toOrderKey = (order: OrderRow): string => {
    return String(order?.number ?? '');
  };

  const normalizeProductsForExport = (rawProducts: Product[]): Product[] => {
    const list = Array.isArray(rawProducts) ? rawProducts : [];

    return list.flatMap((p: Product) => {
      const base = {
        code: p?.code,
        name: p?.name,
        category: p?.category,
        sub_category: p?.sub_category,
        category_id: p?.category_id,
      };

      const lines =
        Array.isArray(p?.lines) && p.lines.length > 0 ? p.lines : null;
      if (lines) {
        const visibleLine = lines.find((l) => l.kind === 'visible');
        if (!visibleLine) return [];
        const qty = Number(visibleLine.quantity ?? 0);
        if (!Number.isFinite(qty) || qty <= 0) return [];
        return [
          { ...base, quantity: qty, is_exchanged_product: false } as Product,
        ];
      }

      if (p?.is_exchanged_product !== false) return [];

      const qty = Number(p?.quantity_total ?? p?.quantity ?? 0);
      if (!Number.isFinite(qty) || qty <= 0) return [];

      return [
        { ...base, quantity: qty, is_exchanged_product: false } as Product,
      ];
    });
  };

  const downloadVisibleOrdersExcel = async (): Promise<void> => {
    try {
      setIsDownloading(true);
      exportCancelledRef.current = false;
      setExportCanClose(false);
      setExportLimitMessage('');
      setExportErrorCount(0);
      setExportProcessedOrders(0);
      setExportStage('Buscando dados para exportação');
      setExportModalOpen(true);
      setExportTotalOrders(2);

      const hasFilters =
        Array.isArray(selectedFilters) && selectedFilters.length > 0;
      let requestNumbers: string[] = [];

      if (!hasFilters) {
        requestNumbers = (Array.isArray(orders) ? orders : [])
          .map((o) => String(o?.number ?? '').trim())
          .filter((n) => n !== '');
      } else {
        const orderDateRange = Array.isArray(
          getFirstFilterValue(selectedFilters, 'Data')
        )
          ? (getFirstFilterValue(selectedFilters, 'Data') as unknown[])
          : undefined;

        const deliveryDateRange = Array.isArray(
          getFirstFilterValue(selectedFilters, 'Estimativa de entrega')
        )
          ? (getFirstFilterValue(
              selectedFilters,
              'Estimativa de entrega'
            ) as unknown[])
          : undefined;
        const orderDateRanges = getOrderDateRangesParam(selectedFilters);
        const deliveryDateRanges = getDeliveryDateRangesParam(selectedFilters);

        const params = {
          page: 0,
          page_size: 9999,
          number: getFilterParam(selectedFilters, 'Nº Pedido'),
          type: getFilterParam(selectedFilters, 'Tipo'),
          status: getFilterParam(selectedFilters, 'Status'),
          cpf: getFilterParam(selectedFilters, 'CPF'),
          cnpj: getEmpresaCnpjParam(selectedFilters),
          company_type: getCompanyTypeParam(selectedFilters),
          patient_name: getFilterParam(selectedFilters, 'Paciente'),
          initial_date: orderDateRange?.[0]
            ? formatDate(orderDateRange[0] as string)
            : undefined,
          final_date: orderDateRange?.[1]
            ? formatDate(orderDateRange[1] as string)
            : undefined,
          date_ranges: orderDateRanges.length > 1 ? orderDateRanges : undefined,
          order_sap_number: getFilterParam(selectedFilters, 'Nº Pedido SAP'),
          client_sap_number: getFilterParam(selectedFilters, 'Nº Cliente SAP'),
          initial_delivery_date: deliveryDateRange?.[0]
            ? formatDate(deliveryDateRange[0] as string)
            : undefined,
          final_delivery_date: deliveryDateRange?.[1]
            ? formatDate(deliveryDateRange[1] as string)
            : undefined,
          delivery_date_ranges:
            deliveryDateRanges.length > 1 ? deliveryDateRanges : undefined,
          alarm: getFilterParam(selectedFilters, 'Sinal luminoso'),
          operator: getFilterParam(selectedFilters, 'Operador Responsável'),
          distribution_center: getFilterParam(
            selectedFilters,
            'Filial Distribuidora'
          ),
          _timestamp: new Date().getTime(),
        };

        const result = await new OrdersDataSource().getAll(params);
        requestNumbers = (
          Array.isArray(result?.data) ? (result.data as OrderRow[]) : []
        )
          .map((o: OrderRow) => String(o?.number ?? '').trim())
          .filter((n: string) => n !== '');
      }

      if (requestNumbers.length === 0) {
        setCurrentAlertError(
          hasFilters
            ? 'Nenhum pedido encontrado para os filtros selecionados.'
            : 'Nenhum pedido visível para exportar.'
        );
        setExportCanClose(true);
        return;
      }

      if (exportCancelledRef.current) {
        setExportStage('Cancelado');
        setCurrentAlertError('Exportação cancelada.');
        setExportCanClose(true);
        return;
      }

      setExportProcessedOrders(1);

      const exportRespRaw = (await exportExcelData({
        requestNumbersTemp: requestNumbers,
      })) as {
        body?: {
          totalExported?: number;
          requesterNameByEmail?: Record<string, string>;
          productsByOrder?: Record<string, Product[]>;
          orders?: OrderRow[];
        };
        totalExported?: number;
        requesterNameByEmail?: Record<string, string>;
        productsByOrder?: Record<string, Product[]>;
        orders?: OrderRow[];
      };

      const exportResp = (exportRespRaw?.body ?? exportRespRaw) as {
        totalExported?: number;
        requesterNameByEmail?: Record<string, string>;
        productsByOrder?: Record<string, Product[]>;
        orders?: OrderRow[];
      };

      const ordersToExport = Array.isArray(exportResp?.orders)
        ? exportResp.orders
        : [];

      if (ordersToExport.length === 0) {
        setCurrentAlertError('Nenhum pedido encontrado para exportar.');
        setExportCanClose(true);
        return;
      }

      const requesterNameByEmail = new Map<string, string>(
        Object.entries(exportResp?.requesterNameByEmail ?? {})
      );

      const productsByOrder = new Map<string, Product[]>(
        Object.entries(exportResp?.productsByOrder ?? {}).map(([k, v]) => [
          k,
          normalizeProductsForExport(Array.isArray(v) ? v : []),
        ])
      );

      setExportStage('Gerando planilha');

      const header = [
        ...(canExportResponsibleOperator ? ['Operador Responsável'] : []),
        'Solicitante',
        'Nº Pedido',
        ...(canExportResponsibleOperator ? ['Nº Pedido SAP'] : []),
        'Tipo',
        'CPF',
        'Nº Cliente SAP',
        'Paciente',
        'CNPJ',
        'Empresa',
        'Data de inclusão',
        'Status',
        ...(canExportResponsibleOperator ? ['Filial Distribuidora'] : []),
        'Estimativa de entrega',
        'Janela de entrega',
        'Produto (código)',
        'Produto (nome)',
        'Categoria',
        'Subcategoria',
        'Quantidade',
      ];

      const rowsForExcel: Record<string, unknown>[] = [];

      ordersToExport.forEach((order: OrderRow) => {
        const sapNumbers = Array.isArray(order?.sap_numbers)
          ? order.sap_numbers.join(', ')
          : String(order?.sap_numbers ?? '');

        const rawRequester = String(order?.created_by ?? '').trim();
        const requester = rawRequester.includes('@')
          ? requesterNameByEmail.get(rawRequester) || rawRequester
          : rawRequester;

        const orderBase = {
          ...(canExportResponsibleOperator
            ? {
                'Operador Responsável':
                  order?.responsible_operator ?? 'Sem responsável',
              }
            : {}),
          Solicitante: requester || '',
          'Nº Pedido': order?.number ?? '',
          ...(canExportResponsibleOperator
            ? { 'Nº Pedido SAP': sapNumbers }
            : {}),
          Tipo: order?.type ?? '',
          CPF: formatDocumentOrDash(order?.customer?.cpf, formatCPF, {
            documentType: 'cpf',
          }),
          'Nº Cliente SAP': order?.customer?.sap ?? '',
          Paciente: order?.customer?.name ?? '',
          CNPJ: order?.customer?.cnpj ?? '',
          Empresa: resolveCompanyName(
            order?.customer?.cnpj ?? '',
            order?.customer?.company ?? order?.customer?.cnpj ?? ''
          ),
          'Data de inclusão': order?.created_at
            ? formatDate(order.created_at, 'DD/MM/YYYY HH:mm:ss')
            : '',
          Status: order?.status?.name ?? '',
          ...(canExportResponsibleOperator
            ? {
                'Filial Distribuidora': order?.distribution_center
                  ? getDistributionCenterName(order.distribution_center)
                  : '',
              }
            : {}),
          'Estimativa de entrega': order?.delivery_forecast ?? '',
          'Janela de entrega': order?.delivery_window ?? '',
        };

        const orderKey = toOrderKey(order);
        const products = productsByOrder.get(orderKey) ?? [];

        if (products.length === 0) {
          rowsForExcel.push({
            ...orderBase,
            'Produto (código)': '',
            'Produto (nome)': '',
            Categoria: '',
            Subcategoria: '',
            Quantidade: '',
          });
          return;
        }

        products.forEach((p: Product) => {
          rowsForExcel.push({
            ...orderBase,
            'Produto (código)': p?.code ?? '',
            'Produto (nome)': p?.name ?? '',
            Categoria: p?.category ?? '',
            Subcategoria: p?.sub_category ?? '',
            Quantidade: String(p?.quantity ?? ''),
          });
        });
      });

      const worksheet = XLSX.utils.json_to_sheet(rowsForExcel, { header });
      worksheet['!cols'] = header.map(() => ({ wch: 22 }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Pedidos');

      const now = new Date();
      const exportDate = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(
        now.getHours()
      ).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(
        now.getSeconds()
      ).padStart(2, '0')}`;

      XLSX.writeFile(workbook, `Pedidos-${exportDate}.xlsx`);

      setExportProcessedOrders(2);
      setExportStage('Concluído');
      setExportCanClose(true);
    } catch (e) {
      console.error('[downloadVisibleOrdersExcel] error', e);
      setCurrentAlertError('Falha ao exportar Excel. Tente novamente.');
      setExportCanClose(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const onOpenAction = async (order: OrderTableData): Promise<void> => {
    setOpenAction(true);
    const proof = await getProofOfDelivery(order.sap_numbers, true);
    if (proof.length === 0) {
      setOpenAction(false);
      setCurrentAlertError('Não foi encontrado nenhum recibo!');
    }
    setProofOfDelivery([...proof]);
  };

  let header: string[] = [
    '',
    'Operador responsável',
    'Nº Pedido',
    'Nº Pedido SAP',
    'Tipo',
    'CPF',
    'Nº Cliente SAP',
    'Paciente',
    'CNPJ',
    'Empresa',
    'Data de inclusão',
    'Status',
    'Filial Distribuidora',
    'Atividade',
  ];

  type TableRow = Record<string, unknown>;

  let data: TableRow[] = orders.map((order: OrderRow) => ({
    expandPanel: (
      <OrderDetails
        order={order}
        onOrderEdit={canEditOrders ? onOrderEdit : undefined}
        hiddenFileInput={hiddenFileInput}
        uploadCECFile={uploadCECFile}
        handleChangeCECFile={handleChangeCECFile}
        isRowExpanded={expandedRows.includes(order.id)}
      />
    ),
    sinalLuminoso: <AlarmLamp alarm={order.alarm} />,
    operadorResponsavel: order.responsible_operator ?? 'Sem responsável',
    numPedido: order.number,
    numPedidoSap:
      order.sap_numbers.length === 0 ? (
        <></>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          {order.sap_numbers?.map((x: string, idx: number) => (
            <div
              key={idx}
              style={{
                display: 'block',
                paddingBottom:
                  idx === order.sap_numbers.length - 1 ? '0px' : '5px',
              }}
            >
              {x}
            </div>
          ))}
        </div>
      ),
    tipo: order.type,
    cpf: formatDocumentOrDash(order.customer.cpf, formatCPF, {
      documentType: 'cpf',
    }),
    numeroSapCliente: order.customer.sap,
    nome: (
      <PatientName
        name={order.customer.name}
        additionalInformation={order.customer.additionalInformation?.map(
          (i) => i.id
        )}
        canSeeFullContent={currentUser.canSeeFullOrdersTable}
      />
    ),
    cnpj: order.customer.cnpj,
    nomeCliente: resolveCompanyName(
      order.customer.cnpj,
      order.customer.company ?? order.customer.cnpj ?? ''
    ),
    dataInclusao: formatDate(order.created_at, 'DD/MM/YYYY HH:mm:ss'),
    status: (
      <Tooltip title={order.status_complement}>
        {orderEnums.status.isConcluded(order.status.id.toString()) ||
        orderEnums.status.isConfirmed(order.status.id.toString()) ? (
          <GreenLabel
            style={{ textTransform: 'capitalize', textAlign: 'center' }}
          >
            {order.status.name}
          </GreenLabel>
        ) : orderEnums.status.isUnsuccessful(order.status.id.toString()) ? (
          <RedLabel
            style={{ textTransform: 'capitalize', textAlign: 'center' }}
          >
            {order.status.name}
          </RedLabel>
        ) : (
          <YellowLabel
            style={{ textTransform: 'capitalize', textAlign: 'center' }}
          >
            {order.status.name}
          </YellowLabel>
        )}
      </Tooltip>
    ),
    distribution_center: getDistributionCenterName(order.distribution_center),
    atividade: Activity(order, onOpenAction),
    showExpandPanelIcon: true,
    addExpandedRow: () => setExpandedRows([...expandedRows, order.id]),
    removeExpandedRow: () =>
      setExpandedRows(expandedRows.filter((x) => x !== order.id)),
  })) as TableRow[];

  if (!currentUser.canSeeFullOrdersTable) {
    header = [
      '',
      'Nº Pedido',
      'Tipo',
      'CPF',
      'Paciente',
      'CNPJ',
      'Empresa',
      'Data de inclusão',
      'Status',
      'Atividade',
    ];
    data = orders.map((order: OrderRow) => ({
      expandPanel: (
        <OrderDetails
          order={order}
          isRowExpanded={expandedRows.includes(order.id)}
        />
      ),
      cancelPedido: (
        <BsCartXFill
          style={{
            fontSize: '14px',
            cursor: 'pointer',
            color: PrimaryTitleGray,
            display: 'block',
          }}
          onClick={() => onCancelOrder(order)}
        />
      ),
      numPedido: order.number,
      tipo: order.type,
      cpf: formatDocumentOrDash(order.customer.cpf, formatCPF, {
        documentType: 'cpf',
      }),
      nome: order.customer.name,
      cnpj: order.customer.cnpj,
      nomeCliente: resolveCompanyName(
        order.customer.cnpj,
        order.customer.company ?? order.customer.cnpj ?? ''
      ),
      dataInclusao: formatDate(order.created_at, 'DD/MM/YYYY HH:mm:ss'),
      status: (
        <Tooltip title={order.status_complement}>
          {orderEnums.status.isConcluded(order.status.id.toString()) ||
          orderEnums.status.isConfirmed(order.status.id.toString()) ? (
            <GreenLabel
              style={{ textTransform: 'capitalize', textAlign: 'center' }}
            >
              {order.status.name}
            </GreenLabel>
          ) : orderEnums.status.isUnsuccessful(order.status.id.toString()) ? (
            <RedLabel
              style={{ textTransform: 'capitalize', textAlign: 'center' }}
            >
              {order.status.name}
            </RedLabel>
          ) : (
            <YellowLabel
              style={{ textTransform: 'capitalize', textAlign: 'center' }}
            >
              {order.status.name}
            </YellowLabel>
          )}
        </Tooltip>
      ),
      atividade: getDeliveryLabel(order, onOpenAction),
      showExpandPanelIcon: true,
      addExpandedRow: () => setExpandedRows([...expandedRows, order.id]),
      removeExpandedRow: () =>
        setExpandedRows(expandedRows.filter((x) => x !== order.id)),
    })) as TableRow[];
  }

  const getFilterList = async (): Promise<FilterListItem[]> => {
    await ensureDistributionCentersLoaded();
    const distributionCenterCodes = getDistributionCentersItems()
      .map((dc: { value: string }) => dc.value)
      .filter((x: string) => x !== 'none');

        let filter: FilterListItem[] = [
            {
                name: 'Tipo',
                operations: [FilterOperations.EQUAL],
                values: [
                    'Assistência técnica',
                    'Implantação',
                    'Recarga',
                    'Recolhimento',
                    'Descartáveis',
                    'Sublocação',
                ],
            },
            { name: 'Nº Pedido', operations: [FilterOperations.EQUAL] },
            { name: 'CPF', operations: [FilterOperations.EQUAL] },
            {
                name: 'Paciente',
                operations: [
                    FilterOperations.EQUAL,
                    FilterOperations.CONTAINS,
                    FilterOperations.STARTS_WITH,
                ],
            },
            {
                name: 'Status',
                operations: [FilterOperations.EQUAL],
                values: ['Em análise', 'Confirmado', 'Concluído', 'Insucesso'],
            },
            {
                name: 'Tipo de empresa',
                operations: [FilterOperations.EQUAL],
                values: ['Público', 'Privado'],
            },
            {
                name: 'Data',
                operations: [FilterOperations.BETWEEN],
                type: FilterValueType.DATE,
            },
            {
                name: 'Estimativa de entrega',
                operations: [FilterOperations.BETWEEN],
                type: FilterValueType.DATE,
            },
            {
                name: 'Filial Distribuidora',
                operations: [FilterOperations.EQUAL],
                values: distributionCenterCodes,
            },
            {
                name: 'Empresa - CNPJ',
                operations: [FilterOperations.EQUAL],
                values: [],
            },
        ];

    if (currentUser.canSeeFullOrdersTable) {
      const alarmTypes = await new AlarmsDataSource().getAlarmTypes();
      const operators = await new UserDataSource().getOperators();

      const alarms: FilterListItem = {
        name: 'Sinal luminoso',
        operations: [FilterOperations.EQUAL],
        values: (alarmTypes as unknown[]).map(
          (a: unknown) => (a as { name: string }).name
        ),
      };

      const responsibleOperator: FilterListItem = {
        name: 'Operador Responsável',
        operations: [FilterOperations.EQUAL],
        values: operators
          .map((op: unknown) => (op as { name: string }).name)
          .sort()
          .concat('Sem responsável'),
      };

      const additionalFilters: FilterListItem[] = [
        { name: 'Nº Pedido SAP', operations: [FilterOperations.EQUAL] },
        { name: 'Nº Cliente SAP', operations: [FilterOperations.EQUAL] },
        alarms,
        responsibleOperator,
      ];

      filter = [...filter, ...additionalFilters];
    }

    return filter;
  };

  const getUniqueSortedValues = (values: unknown[]): string[] => {
    const uniqueSet = new Set<string>(
      values
        .map((v) => (v == null ? '' : String(v).trim()))
        .filter((v) => v !== '')
    );
    return Array.from(uniqueSet).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  };

  const enrichFiltersWithCompaniesData = (
    baseFilters: FilterListItem[],
    companies: unknown[]
  ): FilterListItem[] => {
    const empresaCnpjValues = getUniqueSortedValues(
      (companies ?? []).map((c: unknown) => {
        const empresa = (c as { empresa?: string })?.empresa?.trim() ?? '';
        const cnpj = (c as { cnpj?: string })?.cnpj?.trim() ?? '';
        const cnpjFormatted = cnpj.replace(
          /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
          '$1.$2.$3/$4-$5'
        );
        if (!empresa && !cnpj) return '';
        return empresa && cnpjFormatted
          ? `${empresa} - ${cnpjFormatted}`
          : empresa || cnpjFormatted;
      })
    );

    return (baseFilters ?? []).map((f) => {
      if (f.name === 'Empresa - CNPJ') {
        return { ...f, values: empresaCnpjValues };
      }
      return f;
    });
  };

  useEffect(() => {
    const updateFilter = async () => {
      try {
        const filter = await getFilterList();
        const enrichedFilters = enrichFiltersWithCompaniesData(
          filter,
          companies
        );
        setFilterList(enrichedFilters);
      } catch (error) {
        console.error('Erro ao atualizar filtros:', error);
        const filter = await getFilterList();
        setFilterList(filter);
      }
    };
    updateFilter().catch(console.error);
  }, [currentUser.canSeeFullOrdersTable, companies]);

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ExportOrdersProgressModal
        open={exportModalOpen}
        totalOrders={exportTotalOrders}
        processedOrders={exportProcessedOrders}
        stage={exportStage}
        errorCount={exportErrorCount}
        canClose={exportCanClose}
        limitMessage={exportLimitMessage}
        onCancel={() => {
          exportCancelledRef.current = true;
        }}
        onClose={() => {
          setExportModalOpen(false);
          setExportCanClose(false);
        }}
      />
      <BasicFilterLoose
        fieldList={filterList}
        onSearch={onSearch}
        rightSlot={
          <Tooltip
            title={
              isDownloading
                ? 'Exportando...'
                : 'Download das informações em planilha'
            }
          >
            <LighterGreenButton
              disabled={isDownloading || isLoading}
              onClick={downloadVisibleOrdersExcel}
              style={{ width: '36px', height: '36px' }}
            >
              <HiOutlineDownload style={{ fontSize: '18px' }} />
            </LighterGreenButton>
          </Tooltip>
        }
      />

      {openAction && (
        <ImageSliderPop
          closeSlider={() => {
            setOpenAction(false);
            setProofOfDelivery([]);
          }}
          imagesURL={proofOfDelivery}
        />
      )}

      {!isLoading ? (
        <>
          <div
            className="table-style"
            style={{
              flex: 1,
              width: '100%',
              marginTop: '20px',
              overflow: 'auto',
            }}
          >
            <ExpandableTable expandable header={header} data={data} />
          </div>
        </>
      ) : (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            boxSizing: 'border-box',
          }}
        >
          <Spinner />
        </div>
      )}

      <div
        style={{
          marginTop: '10px',
          minHeight: 'fit-content',
          width: '100%',
          alignSelf: 'center',
        }}
      >
        <BasicPagination
          qtyItems={rows}
          page={Number.isFinite(currentPageNumber) ? currentPageNumber : 0}
          pageSize={
            Number.isFinite(currentPageSizeNumber) && currentPageSizeNumber > 0
              ? currentPageSizeNumber
              : 10
          }
          onChange={(e) => onSearch(null, e)}
        />
      </div>
    </div>
  );
};
